import React, { useState, useRef, useEffect } from 'react';
import '../App.css';
import { supabase } from '../lib/supabase';

const PRODUCT_CONFIG = {
  magnet: { name: 'Magnet', width: '7.5cm', height: '5.1cm', qty: 1 },
  rectangle: { name: 'Rectangle', width: '4.5cm', height: '7cm', qty: 1 },
  photostrip: { name: 'Photostrip (Single)', width: '2.5cm', height: '7.5cm', qty: 1 },
  photostrip_full: { name: 'Photostrip (Double)', width: '5cm', height: '7.5cm', qty: 1 },
  full4r: { name: 'Full 4R', width: '10.2cm', height: '15.2cm', qty: 1 },
  full6r: { name: 'Full 6R', width: '15.2cm', height: '20.3cm', qty: 1 },
};

const PAPER_CONFIG = {
  '4r': { width: '10.2cm', height: '15.2cm' },
  '6r': { width: '15.2cm', height: '20.3cm' },
};

export default function App() {
  const [paperSize, setPaperSize] = useState('4r');
  const [slots, setSlots] = useState([]);
  const [activeSlotId, setActiveSlotId] = useState(null);
  const [paperColor, setPaperColor] = useState('#ffffff');
  
  const dragInfo = useRef({ isDragging: false, startX: 0, startY: 0 });

  const parseCm = (str) => parseFloat(str.replace('cm', ''));
  const CM_TO_PX = 37.7952755906;

  // Logic to calculate initial scale so the image covers the slot area
  const getInitialFit = (imgWidth, imgHeight, slotType) => {
    const config = PRODUCT_CONFIG[slotType];
    const slotW = parseCm(config.width) * CM_TO_PX;
    const slotH = parseCm(config.height) * CM_TO_PX;

    const scaleW = slotW / imgWidth;
    const scaleH = slotH / imgHeight;
    
    // Use Math.max to "fill" the box (crop edges) or Math.min to "fit" (black bars)
    return Math.max(scaleW, scaleH);
  };

  const updateSlot = (id, updates) => {
    setSlots(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const processFile = (file, id) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const slot = slots.find(s => s.id === id);
          const initialScale = getInitialFit(img.width, img.height, slot.type);
          
          updateSlot(id, { 
            img: event.target.result, 
            scale: initialScale, 
            x: 0, 
            y: 0 
          });
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const addProduct = (type) => {
    const config = PRODUCT_CONFIG[type];
    const paper = PAPER_CONFIG[paperSize];
    const isFullProduct = config.name.toLowerCase().includes('full');
    const paperMatch = type.includes(paperSize);

    if (isFullProduct) {
      if (!paperMatch) {
        alert(`⚠️ This ${config.name} is too large for a ${paperSize.toUpperCase()} sheet.`);
        return;
      }
      if (slots.length > 0) {
        alert("⚠️ Full page products require a clear canvas.");
        return;
      }
    } else {
      const maxWidth = parseCm(paper.width) - 0.2;
      const maxHeight = parseCm(paper.height) - 0.2;

      let currentX = 0;
      let currentY = 0;
      let currentRowHeight = 0;

      const projectedSlots = [...slots, ...Array(config.qty).fill({ type })];

      for (const slot of projectedSlots) {
        const itemW = parseCm(PRODUCT_CONFIG[slot.type].width);
        const itemH = parseCm(PRODUCT_CONFIG[slot.type].height);

        if (currentX + itemW > maxWidth + 0.01) {
          currentX = 0;
          currentY += currentRowHeight;
          currentRowHeight = 0;
        }

        currentRowHeight = Math.max(currentRowHeight, itemH);
        currentX += itemW;

        if (currentY + itemH > maxHeight + 0.01) {
          alert(`⚠️ No space left! The ${config.name} won't fit.`);
          return;
        }
      }
    }

    const newSlots = Array.from({ length: config.qty }).map(() => ({
      id: Math.random().toString(36).substr(2, 9),
      type: type,
      img: null,
      scale: 1,
      x: 0,
      y: 0
    }));
    setSlots([...slots, ...newSlots]);
  };

  const handleMouseDown = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    
    const slot = slots.find(s => s.id === id);
    if (!slot || !slot.img) return;

    setActiveSlotId(id);
    dragInfo.current = {
      isDragging: true,
      startX: e.clientX - slot.x,
      startY: e.clientY - slot.y
    };
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragInfo.current.isDragging || !activeSlotId) return;
      updateSlot(activeSlotId, { 
        x: e.clientX - dragInfo.current.startX, 
        y: e.clientY - dragInfo.current.startY 
      });
    };

    const handleMouseUp = () => {
      dragInfo.current.isDragging = false;
      setActiveSlotId(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [activeSlotId]);

  const handleWheel = (e, id) => {
    e.preventDefault();
    const slot = slots.find(s => s.id === id);
    if (!slot || !slot.img) return;

    const delta = e.deltaY * -0.0005; 
    const newScale = Math.min(Math.max(0.01, slot.scale + delta), 5);
    updateSlot(id, { scale: newScale });
  };

  const getOuterCutLines = () => {
    const paper = PAPER_CONFIG[paperSize];

    let currentX = 0;
    let currentY = 0;
    let currentRowHeight = 0;

    const maxWidth = parseCm(paper.width) - 0.2;

    let maxRight = 0;
    let maxBottom = 0;

    slots.forEach((slot) => {
      const config = PRODUCT_CONFIG[slot.type];
      const itemW = parseCm(config.width);
      const itemH = parseCm(config.height);

      if (currentX + itemW > maxWidth + 0.01) {
        currentX = 0;
        currentY += currentRowHeight;
        currentRowHeight = 0;
      }

      const rightEdge = currentX + itemW;
      const bottomEdge = currentY + itemH;

      maxRight = Math.max(maxRight, rightEdge);
      maxBottom = Math.max(maxBottom, bottomEdge);

      currentRowHeight = Math.max(currentRowHeight, itemH);
      currentX += itemW;
    });

    return {
      vertical: maxRight,
      horizontal: maxBottom
    };
  };

  const outerLines = getOuterCutLines();

  const hasFullPageProduct = slots.some(
    s => s.type === 'full4r' || s.type === 'full6r'
  );

  return (
    <div className="app-container" style={{ '--paper-color': paperColor }}>
      <aside className="sidebar">
        <div className="brand-section">
          <img 
            src="https://creativemedia.sg/wp-content/uploads/2024/04/CreativeMedia-White-Logo-No-Padding.svg" 
            alt="Creative Media Logo"
            className="brand-logo"
          />
        </div>

        <button
          className="logout-btn secondary-btn"
          onClick={async () => {
            await supabase.auth.signOut();
          }}
        >
          Logout
        </button>
        
        <div className="control-group">
          <label className="section-label">Paper Size</label>
          <select value={paperSize} onChange={(e) => { setPaperSize(e.target.value); setSlots([]); }}>
            <option value="4r">4R Size</option>
            <option value="6r">6R Size</option>
          </select>
        </div>

        <div className="control-group">
          <label className="section-label">Insert Products</label>
          <div className="btn-grid">
            {Object.keys(PRODUCT_CONFIG).map(key => (
              <button key={key} className="action-btn" onClick={() => addProduct(key)}>
                <span className="plus">+</span> {PRODUCT_CONFIG[key].name}
              </button>
            ))}
          </div>
        </div>

        <div className="control-group">
          <label className="section-label">Paper Appearance</label>
          <div className="color-picker-wrapper">
            <input 
              type="color" 
              value={paperColor} 
              onChange={(e) => setPaperColor(e.target.value)} 
              id="colorPicker"
            />
            <label htmlFor="colorPicker">Background Color</label>
          </div>
          <button className="secondary-btn" onClick={() => setPaperColor('#ffffff')}>
            Reset to White
          </button>
        </div>

        <div className="footer-actions">
          <button className="secondary-btn" onClick={() => setSlots([])}>Reset Canvas</button>
          <button className="primary-btn" onClick={() => window.print()}>Print</button>
        </div>
      </aside>

      <main className="workspace">
        <div 
          className="print-paper" 
          style={{ 
            width: PAPER_CONFIG[paperSize].width, 
            height: PAPER_CONFIG[paperSize].height,
            backgroundColor: paperColor,
            '--paper-color': paperColor
          }}
        >
          {slots.map(slot => (
            <div 
              key={slot.id}
              className={`slot ${slot.type}`}
              style={{ width: PRODUCT_CONFIG[slot.type].width, height: PRODUCT_CONFIG[slot.type].height }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); processFile(e.dataTransfer.files[0], slot.id); }}
              onWheel={(e) => handleWheel(e, slot.id)}
              onClick={() => { if (!slot.img) document.getElementById(`file-${slot.id}`).click(); }}
            >
              <input 
                type="file" id={`file-${slot.id}`} style={{ display: 'none' }} 
                accept="image/*" onChange={(e) => processFile(e.target.files[0], slot.id)}
              />

              {!slot.img && (
                <div className="placeholder">
                  <div className="upload-icon">↑</div>
                  <span>Select Image</span>
                </div>
              )}
              
              {slot.img && (
                <img 
                  src={slot.img} 
                  alt="" 
                  onMouseDown={(e) => handleMouseDown(e, slot.id)}
                  style={{
                    transform: `translate(calc(-50% + ${slot.x}px), calc(-50% + ${slot.y}px)) scale(${slot.scale})`,
                    cursor: activeSlotId === slot.id ? 'grabbing' : 'grab'
                  }}
                />
              )}
              
              <button className="remove-slot" onClick={(e) => {
                e.stopPropagation();
                setSlots(slots.filter(s => s.id !== slot.id));
              }}>✕</button>
            </div>
          ))}

          {slots.length > 0 && !hasFullPageProduct && (
            <>
              {/* Vertical */}
              <div
                className="cut-line vertical"
                style={{ left: `calc(${outerLines.vertical}cm + 0.10cm)` }}
              />

              {/* Horizontal */}
              <div
                className="cut-line horizontal"
                style={{ top: `calc(${outerLines.horizontal}cm + 0.10cm)` }}
              />
            </>
          )}
        </div>
      </main>
    </div>
  );
}